import { format, isValid } from 'date-fns';
import type { MatchResolverContext, ResolverPlayer, ResolverTeam } from 'modules/manager/domain/placeholder';
import { ReviewGoal } from 'modules/match-video/domain/interfaces';
import { DangerPlateData, GoalPlateData, IntroSceneData, SceneTeam } from 'modules/match-video/domain/scenes/scene-data';
import { getDateLocale } from 'shared/i18n/date-locale';

const toSceneTeam = (team: ResolverTeam): SceneTeam => ({ name: team.name, logoUrl: team.logoUrl });

/** «17′», «45+2′» — the protocol minute with its added time. */
export const goalMinuteLabel = (goal: Pick<ReviewGoal, 'minutes' | 'extraMinutes'>): string =>
  goal.extraMinutes > 0 ? `${goal.minutes}+${goal.extraMinutes}′` : `${goal.minutes}′`;

/**
 * The team a goal counts for. The protocol writes an own goal on the player's team (`ReviewGoal.isOwnGoal`), so it
 * goes to the opponent. `undefined` — the event's team is not in the match.
 */
export function goalScoringTeam(context: MatchResolverContext, goal: ReviewGoal): ResolverTeam | undefined {
  const { homeTeam, awayTeam } = context;
  const protocolTeam = goal.teamId === homeTeam.id ? homeTeam : goal.teamId === awayTeam.id ? awayTeam : undefined;
  if (!protocolTeam || !goal.isOwnGoal) return protocolTeam;
  return protocolTeam === homeTeam ? awayTeam : homeTeam;
}

/** The intro of a built-in set: the match as the protocol has it; the dates in the interface language. */
export function toIntroSceneData(context: MatchResolverContext, locale?: string): IntroSceneData {
  const date = context.match.date ? new Date(context.match.date) : null;
  const dateLocale = getDateLocale(locale);
  const hasDate = date !== null && isValid(date);
  return {
    organizerName: context.tournament.name || undefined,
    championshipName: context.championship.name,
    roundName: context.match.roundName || undefined,
    home: toSceneTeam(context.homeTeam),
    away: toSceneTeam(context.awayTeam),
    homeScore: context.match.homeScore,
    awayScore: context.match.awayScore,
    dateLabel: hasDate ? format(date, 'd MMMM yyyy', { locale: dateLocale }) : undefined,
    shortDateLabel: hasDate ? format(date, 'dd.MM.yyyy') : undefined,
  };
}

/**
 * The goal plate of a clip. The team on it is the one the goal counts for (`goalScoringTeam`). No paired goal —
 * nothing is known: the plate is the tag alone.
 */
export function toGoalPlateData(
  context: MatchResolverContext,
  goal: ReviewGoal | undefined,
  players: ReadonlyMap<number, ResolverPlayer>,
): GoalPlateData {
  if (!goal) return {};
  const team = goalScoringTeam(context, goal);
  const scorer = goal.scorerId === undefined ? undefined : players.get(goal.scorerId);
  return {
    team: team ? toSceneTeam(team) : undefined,
    player: scorer?.name ? { name: scorer.name, number: scorer.number } : undefined,
    minuteLabel: goalMinuteLabel(goal),
  };
}

export const toDangerPlateData = (context: MatchResolverContext): DangerPlateData => ({
  home: toSceneTeam(context.homeTeam),
  away: toSceneTeam(context.awayTeam),
});
